// Table View Component - Sortable data table

import { useState } from 'preact/hooks'
import { IconTable, IconCheck, IconPlus } from '../shared/Icons.jsx'
import {
  tableDensity, showColumnPicker, tableSortBy, tableSortOrder, basis, domain,
  tableShowRowNumbers, tableAlternateRowColors, tableShowColumnBorders, tableHoverHighlight, tableRowHeight,
  tableVisibleColumns
} from '../../state/globalSignals.js'
import { isInCompare, addToCompare, removeFromCompare, resetAllFilters } from '../../state/filterStore.js'
import {
  isDemoCatalogUnlocked,
  isCellLockedAtLimit,
  isCellUnlockedToday,
  openLimitModalOrToast,
} from '../../state/demoLimitStore.js'
import { consumeCompareDeepDive, unlockCell } from '../../services/demoLimitService.js'
import LockedMetricToken from '../shared/LockedMetricToken.jsx'
import { isGated, isChemistryLockedForDemo } from '../../utils/metricTiering.js'
import { addedDateSortValue, compareNullsLast, qualitySortValue } from '../../utils/cellSort.js'
import { columnsForDensity } from '../../utils/tableColumns.js'
import { showToast } from '../shared/Toast.jsx'

const ITEMS_PER_PAGE = 50 // 50 rows per page

export default function TableView({ cells, loading, onCellClick, onLockedCellClick }) {
  const [currentPage, setCurrentPage] = useState(1)
  const density = tableDensity.value
  const currentBasis = basis.value
  const currentDomain = domain.value
  
  // ONE answer to "which columns are visible", for every density — see
  // utils/tableColumns.js. Each column used to re-derive it inline, twice per
  // column (header row and body row), from the density plus the custom flags.
  // Ion's set_table_columns needs the same answer — to know what an ADDED column
  // is being added to — and a second derivation in the command layer would have
  // drifted apart on the next column added.
  const visibleColumns = columnsForDensity(density, tableVisibleColumns.value)
  
  // Ensure cells is an array
  const cellsArray = Array.isArray(cells) ? cells : []
  
  // Reset to page 1 when cells change
  // This will be recalculated after sorting
  
  // Only show loading spinner during initial load (when cells array is completely empty)
  // After initial load, show "No Cells Found" for empty filtered results
  if (loading && cellsArray.length === 0) {
    return (
      <div className="loading-spinner">
        <div className="loading-spinner__animation">
          <div className="loading-spinner__ring"></div>
          <div className="loading-spinner__core"></div>
          <div className="loading-spinner__icon">
            <IconTable size={28} />
          </div>
        </div>
        <div className="loading-spinner__text">Loading Table</div>
        <div className="loading-spinner__subtext">Preparing data table...</div>
      </div>
    )
  }
  
  if (!cellsArray || cellsArray.length === 0) {
    return (
      <div className="empty-state-modern">
        <div className="empty-state-card">
          <div className="empty-state-icon-wrapper">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="3" y="3" width="18" height="18" rx="2"></rect>
              <path d="M3 9h18"></path>
              <path d="M9 3v18"></path>
            </svg>
          </div>
          <h3 className="empty-state-title">No Cells Found</h3>
          <p className="empty-state-description">
            No battery cells match your current filters.
            <br />
            Try adjusting or clearing some filters to see more results.
          </p>
          <button 
            className="empty-state-action"
            onClick={() => resetAllFilters()}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 6h18"></path>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
            </svg>
            Clear All Filters
          </button>
        </div>
      </div>
    )
  }
  
  // Governing-rule soft friction (Decision 1A): a demo user clicking a locked
  // premium-metric token or the locked chemistry cell unlocks the WHOLE cell
  // in place via the same Deep-Dive token as opening its detail page — not
  // the PRO-upsell path (that stays reserved for the genuine free/expired
  // shadow-lock, routed through onLockedCellClick as before). unlockCell
  // handles a 429 (at today's limit) itself by opening the conversion modal;
  // any other error is caught here so a transient failure never crashes the
  // row. Always stops propagation first so the surrounding row/token click
  // handler never also fires (see the onClickCapture usage below).
  const handleUnlockClick = async (e, cell) => {
    e.stopPropagation()
    try {
      await unlockCell({ productId: cell.id, surface: 'table' })
    } catch (err) {
      // 429 (DEMO_LIMIT) is handled inside unlockCell itself (opens the
      // conversion modal) and never reaches here. Any other failure gets a
      // visible toast instead of a silent console.warn.
      showToast("Couldn't unlock this cell. Please try again.")
    }
  }

  // True while the Batch Var. / Post-Formation Loss cell currently renders a
  // LockedMetricToken for a DEMO user (band or gated, not yet unlocked today).
  // Drives whether the <td> gets the demo unlock capture-handler instead of
  // letting the token's own click open the PRO upsell.
  const isBatchTokenLockedForDemo = (cell) =>
    isDemoCatalogUnlocked() &&
    !isCellUnlockedToday(cell.id) &&
    cell.batch?.CAP_spread_std_rel == null &&
    (!!cell.batch?.CAP_spread_band || isGated(cell, 'batch.CAP_spread_std_rel'))

  const isAgingTokenLockedForDemo = (cell) =>
    isDemoCatalogUnlocked() &&
    !isCellUnlockedToday(cell.id) &&
    cell.aging?.CAP_loss_rel == null &&
    (!!cell.aging?.CAP_loss_band || isGated(cell, 'aging.CAP_loss_rel'))

  const handleRowClick = (cell, event) => {
    const row = event.currentTarget
    // Bug B: DEMO users at today's deep-dive limit — cells not yet opened
    // today open the DailyLimitModal instead of navigating/fetching.
    if (isCellLockedAtLimit(cell.id)) {
      openLimitModalOrToast({})
      return
    }
    // DEMO users get the full catalog unlocked (subject to the daily view
    // quota) — only shadow-lock genuine free/expired tiers.
    if (cell.is_locked && !isDemoCatalogUnlocked()) {
      if (onLockedCellClick) onLockedCellClick(row, cell)
    } else if (onCellClick) {
      onCellClick(row, cell)
    }
  }
  
  // Determine columns based on density and current domain
  const getEnergyValue = (cell) => {
    if (currentBasis === 'abs') return cell.electrical?.E_c10_Wh ? cell.electrical.E_c10_Wh.toFixed(1) : '—'
    if (currentBasis === 'grav') return cell.electrical?.E_grav_Wh_per_kg ? cell.electrical.E_grav_Wh_per_kg.toFixed(1) : '—'
    if (currentBasis === 'vol') return cell.electrical?.E_vol_Wh_per_L ? cell.electrical.E_vol_Wh_per_L.toFixed(1) : '—'
    return '—'
  }
  
  const getPowerValue = (cell) => {
    if (currentBasis === 'abs') return cell.electrical?.P_peak_W ? cell.electrical.P_peak_W.toFixed(1) : '—'
    if (currentBasis === 'grav') return cell.electrical?.P_grav_W_per_kg ? cell.electrical.P_grav_W_per_kg.toFixed(0) : '—'
    if (currentBasis === 'vol') return cell.electrical?.P_vol_W_per_L ? cell.electrical.P_vol_W_per_L.toFixed(0) : '—'
    return '—'
  }
  
  const getCapacityValue = (cell) => {
    if (currentBasis === 'abs') return cell.electrical?.C_c10_Ah ? cell.electrical.C_c10_Ah.toFixed(2) : '—'
    if (currentBasis === 'grav') return cell.electrical?.C_grav_Ah_per_kg ? cell.electrical.C_grav_Ah_per_kg.toFixed(1) : '—'
    if (currentBasis === 'vol') return cell.electrical?.C_vol_Ah_per_L ? cell.electrical.C_vol_Ah_per_L.toFixed(0) : '—'
    return '—'
  }
  
  const getCurrentValue = (cell) => {
    if (currentBasis === 'abs') return cell.electrical?.I_peak_A ? cell.electrical.I_peak_A.toFixed(1) : '—'
    if (currentBasis === 'grav') return cell.electrical?.I_grav_A_per_kg ? cell.electrical.I_grav_A_per_kg.toFixed(0) : '—'
    if (currentBasis === 'vol') return cell.electrical?.I_vol_A_per_L ? cell.electrical.I_vol_A_per_L.toFixed(0) : '—'
    return '—'
  }
  
  const getNominalCapacity = (cell) => {
    // Nominal capacity in Ah (C10 discharge rate)
    return cell.electrical?.C_c10_Ah ? cell.electrical.C_c10_Ah.toFixed(2) : '—'
  }
  
  const getEnergyUnit = () => {
    if (currentBasis === 'abs') return 'Wh'
    if (currentBasis === 'grav') return 'Wh/kg'
    if (currentBasis === 'vol') return 'Wh/L'
    return ''
  }
  
  const getPowerUnit = () => {
    if (currentBasis === 'abs') return 'W'
    if (currentBasis === 'grav') return 'W/kg'
    if (currentBasis === 'vol') return 'W/L'
    return ''
  }
  
  const getCapacityUnit = () => {
    if (currentBasis === 'abs') return 'Ah'
    if (currentBasis === 'grav') return 'Ah/kg'
    if (currentBasis === 'vol') return 'Ah/L'
    return ''
  }
  
  const getCurrentUnit = () => {
    if (currentBasis === 'abs') return 'A'
    if (currentBasis === 'grav') return 'A/kg'
    if (currentBasis === 'vol') return 'A/L'
    return ''
  }

  // ── Premium quality columns (metric-tiering) ─────────────────────────
  // For DEMO users the exact batch/aging pct is withheld but a coarse teaser
  // band comes through — render a band token instead of a bare "—". Never gate
  // off `isLocked` for the value here (a demo cell is browsable); the tier state
  // is the band / `_gated`. `isLocked` (genuine free/expired tier) → real "—".
  const renderBatchVar = (cell, isLocked) => {
    if (isLocked) return '—'
    const rel = cell.batch?.CAP_spread_std_rel
    if (rel != null) return (rel * 100).toFixed(2)
    // Safeguard (governing rule, point 3): once the cell is unlocked today,
    // never keep showing a lock/band token even if a stale cached row hasn't
    // refreshed with the exact value yet — fall through to genuine "—"
    // instead of a misleading PRO chip on an unlocked cell.
    const unlockedToday = isCellUnlockedToday(cell.id)
    if (cell.batch?.CAP_spread_band && !unlockedToday) {
      return (
        <LockedMetricToken
          band={cell.batch.CAP_spread_band}
          category="quality"
          label="Spread"
        />
      )
    }
    if (isGated(cell, 'batch.CAP_spread_std_rel') && !unlockedToday) {
      // unlockAction must mirror isBatchTokenLockedForDemo below: the <td>
      // only gets the onClickCapture → handleUnlockClick (real Deep-Dive
      // unlock) override for a demo viewer; a non-demo viewer with a gated
      // field (e.g. the PRO-tier robustness test) falls through to this
      // token's own onActivate (PRO upsell) — so the label must say "PRO"
      // there, not promise a deep-dive the click doesn't perform.
      return (
        <LockedMetricToken
          gated
          unit="%"
          category="quality"
          unlockAction={isBatchTokenLockedForDemo(cell) ? 'deep-dive' : 'pro'}
        />
      )
    }
    return '—' // genuine absence
  }

  const renderAging = (cell, isLocked) => {
    if (isLocked) return '—'
    const rel = cell.aging?.CAP_loss_rel
    if (rel != null) {
      return rel < 0
        ? `${(rel * 100).toFixed(2)}` // Negative = capacity gain
        : rel > 0
        ? `+${(rel * 100).toFixed(2)}` // Positive = capacity loss
        : '0.00' // Exactly 0
    }
    // Safeguard (governing rule, point 3): see renderBatchVar above.
    const unlockedToday = isCellUnlockedToday(cell.id)
    if (cell.aging?.CAP_loss_band && !unlockedToday) {
      return (
        <LockedMetricToken band={cell.aging.CAP_loss_band} category="quality" label="Loss" />
      )
    }
    if (isGated(cell, 'aging.CAP_loss_rel') && !unlockedToday) {
      // See renderBatchVar above — unlockAction must mirror
      // isAgingTokenLockedForDemo (the actual onClickCapture guard).
      return (
        <LockedMetricToken
          gated
          unit="%"
          category="quality"
          unlockAction={isAgingTokenLockedForDemo(cell) ? 'deep-dive' : 'pro'}
        />
      )
    }
    return '—' // genuine absence
  }

  // Toggle column visibility (no longer needed as we use signals)
  // Replaced with signal-based column visibility
  
  // Sorting logic: demo cells first, then locked cells; user sort within each group
  const sortedCells = [...cellsArray].sort((a, b) => {
    // Primary sort: demo cells (is_locked=false) before locked cells
    if (a.is_locked !== b.is_locked) return a.is_locked ? 1 : -1

    let aVal, bVal

    switch (tableSortBy.value) {
      case 'name':
        aVal = (a.name || '').toLowerCase()
        bVal = (b.name || '').toLowerCase()
        break
      case 'manufacturer':
        aVal = (a.manufacturer || '').toLowerCase()
        bVal = (b.manufacturer || '').toLowerCase()
        break
      case 'format':
        aVal = (a.mechanical?.format_group || a.mechanical?.format || '').toLowerCase()
        bVal = (b.mechanical?.format_group || b.mechanical?.format || '').toLowerCase()
        break
      case 'nomCapacity':
        aVal = parseFloat(a.electrical?.C_c10_Ah || 0)
        bVal = parseFloat(b.electrical?.C_c10_Ah || 0)
        break
      case 'energy':
        if (currentBasis === 'abs') {
          aVal = parseFloat(a.electrical?.E_c10_Wh || 0)
          bVal = parseFloat(b.electrical?.E_c10_Wh || 0)
        } else if (currentBasis === 'grav') {
          aVal = parseFloat(a.electrical?.E_grav_Wh_per_kg || 0)
          bVal = parseFloat(b.electrical?.E_grav_Wh_per_kg || 0)
        } else if (currentBasis === 'vol') {
          aVal = parseFloat(a.electrical?.E_vol_Wh_per_L || 0)
          bVal = parseFloat(b.electrical?.E_vol_Wh_per_L || 0)
        }
        break
      case 'power':
        if (currentBasis === 'abs') {
          aVal = parseFloat(a.electrical?.P_peak_W || 0)
          bVal = parseFloat(b.electrical?.P_peak_W || 0)
        } else if (currentBasis === 'grav') {
          aVal = parseFloat(a.electrical?.P_grav_W_per_kg || 0)
          bVal = parseFloat(b.electrical?.P_grav_W_per_kg || 0)
        } else if (currentBasis === 'vol') {
          aVal = parseFloat(a.electrical?.P_vol_W_per_L || 0)
          bVal = parseFloat(b.electrical?.P_vol_W_per_L || 0)
        }
        break
      case 'capacity':
        if (currentBasis === 'abs') {
          aVal = parseFloat(a.electrical?.C_c10_Ah || 0)
          bVal = parseFloat(b.electrical?.C_c10_Ah || 0)
        } else if (currentBasis === 'grav') {
          aVal = parseFloat(a.electrical?.C_grav_Ah_per_kg || 0)
          bVal = parseFloat(b.electrical?.C_grav_Ah_per_kg || 0)
        } else if (currentBasis === 'vol') {
          aVal = parseFloat(a.electrical?.C_vol_Ah_per_L || 0)
          bVal = parseFloat(b.electrical?.C_vol_Ah_per_L || 0)
        }
        break
      case 'current':
        if (currentBasis === 'abs') {
          aVal = parseFloat(a.electrical?.I_peak_A || 0)
          bVal = parseFloat(b.electrical?.I_peak_A || 0)
        } else if (currentBasis === 'grav') {
          aVal = parseFloat(a.electrical?.I_grav_A_per_kg || 0)
          bVal = parseFloat(b.electrical?.I_grav_A_per_kg || 0)
        } else if (currentBasis === 'vol') {
          aVal = parseFloat(a.electrical?.I_vol_A_per_L || 0)
          bVal = parseFloat(b.electrical?.I_vol_A_per_L || 0)
        }
        break
      case 'year':
        // The MANUFACTURER'S RELEASE year — not when Batemo added the cell
        // (that is 'addedDate' below).
        aVal = a.year || 1900
        bVal = b.year || 1900
        break
      case 'aging':
      case 'batchVariation':
        // Returns EARLY with the order already applied — see the same case in
        // GridView.jsx and utils/cellSort.js: nulls must stay last in both
        // directions, which the asc/desc comparison below would undo.
        return compareNullsLast(
          qualitySortValue(a, tableSortBy.value),
          qualitySortValue(b, tableSortBy.value),
          tableSortOrder.value,
        )
      case 'addedDate':
        // Catalogue-INSERTION date. See the same case in GridView.jsx — returns
        // EARLY with the order already applied, so cells without an insertion
        // date stay last in both directions.
        return compareNullsLast(
          addedDateSortValue(a),
          addedDateSortValue(b),
          tableSortOrder.value
        )
      default:
        return 0
    }
    
    if (aVal < bVal) return tableSortOrder.value === 'asc' ? -1 : 1
    if (aVal > bVal) return tableSortOrder.value === 'asc' ? 1 : -1
    return 0
  })
  
  // Pagination on sorted data
  const totalPages = Math.ceil(sortedCells.length / ITEMS_PER_PAGE)
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
  const endIndex = startIndex + ITEMS_PER_PAGE
  const paginatedCells = sortedCells.slice(startIndex, endIndex)
  
  // Reset to page 1 when cells change or sort changes
  if (currentPage > totalPages && totalPages > 0) {
    setCurrentPage(1)
  }
  
  // Function to handle header click for sorting
  const handleHeaderClick = (column) => {
    if (tableSortBy.value === column) {
      // Toggle sort order if clicking same column
      tableSortOrder.value = tableSortOrder.value === 'asc' ? 'desc' : 'asc'
    } else {
      // Sort by new column, ascending
      tableSortBy.value = column
      tableSortOrder.value = 'asc'
    }
  }
  
  // Helper to render sort indicator
  const getSortIndicator = (column) => {
    if (tableSortBy.value !== column) return ''
    return tableSortOrder.value === 'asc' ? ' ▲' : ' ▼'
  }

  return (
    <div className="cells-table-view">
      {/* Column Picker is now in Context Bar as Dropdown */}
      <div
        className="cells-table-container" 
        data-row-height={tableRowHeight.value}
        data-alternate-rows={tableAlternateRowColors.value}
        data-column-borders={tableShowColumnBorders.value}
        data-hover-highlight={tableHoverHighlight.value}
      >
        <table className="cells-table">
          <thead>
            <tr>
              <th className="compare-column-header" title="Compare">
                {/* Empty or icon for compare column */}
              </th>
              {tableShowRowNumbers.value && <th className="row-number-header">#</th>}
              {visibleColumns.name && (
                <th onClick={() => handleHeaderClick('name')} className="sortable-header">
                  Name{getSortIndicator('name')}
                </th>
              )}
              {visibleColumns.manufacturer && (
                <th onClick={() => handleHeaderClick('manufacturer')} className="sortable-header">
                  Manufacturer{getSortIndicator('manufacturer')}
                </th>
              )}
              {visibleColumns.format && (
                <th onClick={() => handleHeaderClick('format')} className="sortable-header">
                  Format{getSortIndicator('format')}
                </th>
              )}
              {visibleColumns.nomCapacity && (
                <th onClick={() => handleHeaderClick('nomCapacity')} className="sortable-header">
                  Nom. Capacity <span className="unit-label">(Ah)</span>{getSortIndicator('nomCapacity')}
                </th>
              )}
              {visibleColumns.energyPower && (
                currentDomain === 'energy-power' ? (
                  <>
                    <th onClick={() => handleHeaderClick('energy')} className="sortable-header">
                      Energy <span className="unit-label">({getEnergyUnit()})</span>{getSortIndicator('energy')}
                    </th>
                    <th onClick={() => handleHeaderClick('power')} className="sortable-header">
                      Power <span className="unit-label">({getPowerUnit()})</span>{getSortIndicator('power')}
                    </th>
                  </>
                ) : (
                  <>
                    <th onClick={() => handleHeaderClick('capacity')} className="sortable-header">
                      Capacity <span className="unit-label">({getCapacityUnit()})</span>
                    </th>
                    <th onClick={() => handleHeaderClick('current')} className="sortable-header">
                      Current <span className="unit-label">({getCurrentUnit()})</span>
                    </th>
                  </>
                )
              )}
              {visibleColumns.batchVar && <th>Batch Var. <span className="unit-label">(%)</span></th>}
              {visibleColumns.aging && <th>Post-Formation Loss <span className="unit-label">(%)</span></th>}
              {visibleColumns.chemistry && <th>Chemistry</th>}
              {visibleColumns.voltage && <th>Voltage <span className="unit-label">(V)</span></th>}
              {visibleColumns.mass && <th>Mass <span className="unit-label">(g)</span></th>}
            </tr>
          </thead>
          <tbody>
            {paginatedCells.map((cell, index) => {
              const rowNumber = startIndex + index + 1
              const isLocked = cell.is_locked && !isDemoCatalogUnlocked()
              // Bug B: not-yet-opened cell at today's deep-dive limit.
              // Distinct from (and mutually exclusive with) the shadow-lock
              // above — only DEMO users at the limit get this.
              const atLimitLocked = isCellLockedAtLimit(cell.id)
              const inCompare = !isLocked && isInCompare(cell.id)
              return (
                <tr
                  key={cell.id}
                  className={`${isLocked ? 'row--locked' : ''} ${atLimitLocked ? 'row--limit-locked' : ''}`}
                  onClick={(e) => handleRowClick(cell, e)}
                  style={{ cursor: isLocked ? 'not-allowed' : 'pointer' }}
                >
                  <td className="compare-column-cell">
                    {atLimitLocked ? (
                      <span className="lock-icon" aria-label="Deep-dive limit reached — resets soon" title="Deep-dive limit reached — resets soon">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                        </svg>
                      </span>
                    ) : isLocked ? (
                      <span className="lock-icon" title="Upgrade to unlock this cell">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                        </svg>
                      </span>
                    ) : (
                      <button
                        className={`compare-checkbox ${inCompare ? 'active' : ''}`}
                        onClick={async (e) => {
                          e.stopPropagation()
                          // Removing never counts a deep-dive.
                          if (inCompare) {
                            removeFromCompare(cell.id)
                            return
                          }
                          // Adding: eager deep-dive gate (counts live for demo,
                          // blocks at the limit) — symmetric with Quick-Add.
                          const ok = await consumeCompareDeepDive({
                            id: cell.id,
                            name: cell.name,
                            manufacturer: cell.manufacturer,
                          })
                          if (ok) addToCompare(cell.id)
                        }}
                        title={inCompare ? 'Remove from comparison' : 'Add to comparison'}
                      >
                        {inCompare ? <IconCheck size={14} /> : <IconPlus size={14} />}
                      </button>
                    )}
                  </td>
                  {tableShowRowNumbers.value && <td className="row-number">{rowNumber}</td>}
                  {visibleColumns.name && <td className="cell-name">{cell.name || cell.id}</td>}
                  {visibleColumns.manufacturer && <td>{cell.manufacturer || 'Unknown'}</td>}
                  {visibleColumns.format && <td>{cell.mechanical?.format_group || cell.mechanical?.format || 'N/A'}</td>}
                  {visibleColumns.nomCapacity && <td>{getNominalCapacity(cell)}</td>}
                  {visibleColumns.energyPower && (
                    currentDomain === 'energy-power' ? (
                      <>
                        <td>{getEnergyValue(cell)}</td>
                        <td>{getPowerValue(cell)}</td>
                      </>
                    ) : (
                      <>
                        <td>{getCapacityValue(cell)}</td>
                        <td>{getCurrentValue(cell)}</td>
                      </>
                    )
                  )}
                  {visibleColumns.batchVar && (
                    <td
                      {...(!isLocked && isBatchTokenLockedForDemo(cell)
                        ? { onClickCapture: (e) => handleUnlockClick(e, cell) }
                        : {})}
                    >
                      {renderBatchVar(cell, isLocked)}
                    </td>
                  )}
                  {visibleColumns.aging && (
                    <td
                      {...(!isLocked && isAgingTokenLockedForDemo(cell)
                        ? { onClickCapture: (e) => handleUnlockClick(e, cell) }
                        : {})}
                    >
                      {renderAging(cell, isLocked)}
                    </td>
                  )}
                  {visibleColumns.chemistry && (
                    <td>
                      {isLocked ? (
                        '—'
                      ) : isChemistryLockedForDemo(cell) ? (
                        <span
                          className="locked-metric locked-metric--gated"
                          role="button"
                          tabIndex={0}
                          title="Unlock this cell to see its chemistry — costs 1 deep dive"
                          aria-label="Chemistry locked — unlock this cell, 1 deep dive"
                          onClick={(e) => handleUnlockClick(e, cell)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault()
                              handleUnlockClick(e, cell)
                            }
                          }}
                        >
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                          </svg>
                          {/* isChemistryLockedForDemo (this branch) is only
                              ever true for demo users — the Table chemistry
                              lock has no genuine PRO-upsell variant, so the
                              label is unconditionally "Unlock". */}
                          <span className="locked-metric__pro">Unlock</span>
                          {/* Visible cost hint (Task A2 follow-up) — title/aria
                              alone is invisible on touch and needs a hover on
                              desktop. */}
                          <span className="locked-metric__cost">· 1 deep dive</span>
                        </span>
                      ) : (
                        cell.chemical?.main_chemistry || cell.chemical?.cathode || 'N/A'
                      )}
                    </td>
                  )}
                  {visibleColumns.voltage && (
                    <td>{isLocked ? '—' : (cell.electrical?.V_mean ? cell.electrical.V_mean.toFixed(2) : '—')}</td>
                  )}
                  {visibleColumns.mass && (
                    <td>{isLocked ? '—' : (cell.mechanical?.mass_g ? cell.mechanical.mass_g.toFixed(1) : '—')}</td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      
      {/* Pagination Controls */}
      {totalPages >= 1 && (
        <div className="pagination-controls">
          <button
            className="pagination-btn pagination-btn--first"
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
            title="First page"
          >
            ««
          </button>
          
          <button
            className="pagination-btn pagination-btn--prev"
            onClick={() => setCurrentPage(currentPage - 1)}
            disabled={currentPage === 1}
            title="Previous page"
          >
            ‹ Prev
          </button>
          
          <div className="pagination-info">
            <span className="pagination-current">Page {currentPage}</span>
            <span className="pagination-divider">/</span>
            <span className="pagination-total">{totalPages}</span>
            <span className="pagination-count">
              ({startIndex + 1}–{Math.min(endIndex, sortedCells.length)} of {sortedCells.length})
            </span>
          </div>
          
          <button
            className="pagination-btn pagination-btn--next"
            onClick={() => setCurrentPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            title="Next page"
          >
            Next ›
          </button>
          
          <button
            className="pagination-btn pagination-btn--last"
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
            title="Last page"
          >
            »»
          </button>
        </div>
      )}
    </div>
  )
}
